import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { MilestoneStatus, ProjectStatus, PresenceStatus } from '../types';
import { X } from 'lucide-react';

// --- Button ---
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  isLoading,
  ...props
}) => {
  const baseStyle = "inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none rounded-md select-none disabled:opacity-50 disabled:cursor-not-allowed";

  const variants = {
    primary: "bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white shadow-sm",
    secondary: "bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:border-zinc-800 dark:hover:bg-zinc-800",
    ghost: "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800",
    danger: "text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/30",
    outline: "border border-zinc-300 text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-900"
  };

  const sizes = {
    sm: "px-3 py-1.5 text-[12px]",
    md: "px-4 py-2 text-[13px]",
    lg: "px-6 py-3 text-[14px]"
  };

  return (
    <button
      className={`${baseStyle} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {isLoading ? <span className="opacity-0">{children}</span> : children}
      {isLoading && <div className="absolute inset-0 flex items-center justify-center"><div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /></div>}
    </button>
  );
};

// --- Badge ---
export const StatusBadge: React.FC<{ status: ProjectStatus | MilestoneStatus }> = ({ status }) => {
  const styles: Record<string, string> = {
    [ProjectStatus.ACTIVE]: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
    [ProjectStatus.WAITING_ON_RECEIVER]: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
    [ProjectStatus.COMPLETED]: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
    [ProjectStatus.ARCHIVED]: "bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700",
    [ProjectStatus.DRAFT]: "bg-zinc-100 text-zinc-600 border-zinc-200 border-dashed dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700",

    [MilestoneStatus.NOT_STARTED]: "bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700",
    [MilestoneStatus.IN_PROGRESS]: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
    [MilestoneStatus.REVIEW]: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
    [MilestoneStatus.APPROVED]: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
    // MilestoneStatus.DRAFT is omitted because it has the same value 'DRAFT' as ProjectStatus.DRAFT
  };

  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium border uppercase tracking-wide whitespace-nowrap ${styles[status]}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

export const PresenceBadge: React.FC<{ status: PresenceStatus, showLabel?: boolean }> = ({ status, showLabel = true }) => {
  const colors = {
    [PresenceStatus.ACTIVE]: 'bg-emerald-500',
    [PresenceStatus.FOCUSED]: 'bg-blue-500',
    [PresenceStatus.AWAY]: 'bg-amber-500',
    [PresenceStatus.OFFLINE]: 'bg-zinc-400 dark:bg-zinc-600',
  };
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${colors[status]}`}></div>
      {showLabel && <span className="text-[11px] text-zinc-600 dark:text-zinc-400 capitalize">{status.toLowerCase()}</span>}
    </div>
  )
}

// --- Input ---
export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    className={`w-full px-3 py-2 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-md text-[13px] text-zinc-900 dark:text-zinc-200 placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:border-zinc-400 dark:focus:border-zinc-600 transition-colors ${props.className}`}
  />
);

export const TextArea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <textarea
    {...props}
    className={`w-full px-3 py-2 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-md text-[13px] text-zinc-900 dark:text-zinc-200 placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:border-zinc-400 dark:focus:border-zinc-600 transition-colors resize-none ${props.className}`}
  />
);

// --- Switch ---
export const Switch: React.FC<{ checked: boolean; onChange: (checked: boolean) => void }> = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${checked ? 'bg-zinc-900 dark:bg-zinc-100' : 'bg-zinc-200 dark:bg-zinc-700'}`}
  >
    <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-4 bg-white dark:bg-black' : 'translate-x-0'}`} />
  </button>
);

// --- Card ---
export const Card: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => (
  <div className={`bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl ${className || ''}`} {...props}>
    {children}
  </div>
);

// --- Modal ---
export const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; maxWidth?: string; noPadding?: boolean }> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-md',
  noPadding = false
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${maxWidth} bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-2xl animate-fade-in overflow-hidden`}>
        {!noPadding && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-900">
            <h3 className="font-medium text-zinc-900 dark:text-zinc-100">{title}</h3>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100">
              <X size={18} />
            </button>
          </div>
        )}
        <div className={noPadding ? '' : 'p-0'}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

// --- Drawer ---
export const Drawer: React.FC<{ isOpen: boolean; onClose: () => void; title?: string; children: React.ReactNode }> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-white dark:bg-zinc-950 border-l border-zinc-200 dark:border-zinc-800 shadow-2xl flex flex-col animate-fade-in">
        {(title || onClose != null) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-900 shrink-0">
            {title && <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">{title}</h2>}
            <button onClick={onClose} className="p-1 rounded-md text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors">
              <X size={20} />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

// --- Loading ---
export const Loading: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="w-48 h-48 max-w-full">
        <DotLottieReact
          src="https://lottie.host/51f11885-ec59-4c6f-b7dc-948512e149aa/CLxoRv3fJB.lottie"
          loop
          autoplay
        />
      </div>
    </div>
  );
};